import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EmptyState, Spinner } from '../components/Layout';
import { watchFolders as api, errorMessage } from '../utils/api';
import { formatDateTime, formatNumber, isolate } from '../utils/format';

/*
 * مسیرهای پایش عامل آپلود.
 *
 * عامل روی کامپیوتر تصویربرداری اجرا می‌شود و این مسیرها آنجا هستند، نه
 * روی سرور. پس سامانه نمی‌تواند وجودشان را بررسی کند — فقط شکل ظاهری
 * مسیر را می‌سنجد و بقیه را از گزارش خود عامل نشان می‌دهد.
 *
 * ستون «وضعیت» عمداً بین «هنوز گشتی نزده» و «خطا داد» تفاوت می‌گذارد:
 * «فولدر خالی بود» و «نتوانستم فولدر را ببینم» دو چیز کاملاً متفاوت‌اند و
 * قاطی کردنشان یک‌بار دو روز وقت گرفت.
 */

// بک‌اسلش‌ها دوتایی‌اند چون در رشته‌ی جاوااسکریپت‌اند؛ تک‌تایی، `\2` را
// دنباله‌ی هشت‌هشتی می‌خواند.
const PATH_EXAMPLE = 'S:\\CBCT share\\2-dicom export\\EXPORT-DICOM';

const HEALTH = {
  ok: { label: 'سالم', tone: 'success' },
  pending: { label: 'هنوز گشتی نزده', tone: 'muted' },
  error: { label: 'خطا', tone: 'danger' },
  off: { label: 'غیرفعال', tone: 'muted' },
};

export default function WatchFolders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.list();
      setRows(data.results ?? data);
    } catch (error) {
      toast.error(errorMessage(error, 'فهرست مسیرها بارگذاری نشد.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.create({ path: path.trim(), label: label.trim() });
      setPath('');
      setLabel('');
      toast.success('مسیر اضافه شد. عامل در گشت بعدی آن را می‌بیند.');
      await load();
    } catch (error) {
      toast.error(errorMessage(error, 'مسیر ثبت نشد.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.update(row.id, { is_active: !row.is_active });
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`مسیر «${isolate(row.path)}» حذف شود؟`)) return;
    try {
      await api.remove(row.id);
      toast.success('مسیر حذف شد.');
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <section className="panel">
      <h2 className="panel__title">مسیرهای پایش عامل آپلود</h2>
      <p className="form__note">
        عامل روی کامپیوتر تصویربرداری این مسیرها را می‌پاید و مطالعه‌های تازه
        را خودش بالا می‌فرستد. مسیرها روی همان کامپیوترند، پس سامانه نمی‌تواند
        وجودشان را بررسی کند — وضعیت هر مسیر را خودِ عامل گزارش می‌دهد.
      </p>

      <form className="toolbar" onSubmit={add}>
        <label className="field field--inline field--grow">
          <span>مسیر</span>
          <input
            className="input"
            dir="ltr"
            placeholder={PATH_EXAMPLE}
            value={path}
            onChange={(event) => setPath(event.target.value)}
            required
          />
        </label>
        <label className="field field--inline">
          <span>توضیح</span>
          <input
            className="input"
            placeholder="اختیاری"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
        <button type="submit" className="btn btn--primary" disabled={saving}>
          افزودن مسیر
        </button>
      </form>

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="📁"
          title="هنوز مسیری تعریف نشده است."
          hint="تا مسیری تعریف نشود، عامل چیزی برای پاییدن ندارد."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>مسیر</th>
                <th>وضعیت</th>
                <th>آخرین گشت</th>
                <th>مطالعه‌های یافته</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const health = HEALTH[row.health] || HEALTH.pending;
                return (
                  <tr key={row.id} className={row.is_active ? '' : 'row--muted'}>
                    <td>
                      <span className="watch__path" dir="ltr">{row.path}</span>
                      {row.label && <><br /><small className="muted">{row.label}</small></>}
                    </td>
                    <td>
                      <span className={`badge badge--${health.tone}`}>{health.label}</span>
                      {row.last_error && (
                        <>
                          <br />
                          <small className="muted">{row.last_error}</small>
                        </>
                      )}
                    </td>
                    <td>{row.last_seen_at ? formatDateTime(row.last_seen_at) : '—'}</td>
                    <td className="num">{formatNumber(row.found_count)}</td>
                    <td className="cell--actions">
                      <button type="button" className="link" onClick={() => toggle(row)}>
                        {row.is_active ? 'غیرفعال کردن' : 'فعال کردن'}
                      </button>
                      <button
                        type="button"
                        className="link link--danger"
                        onClick={() => remove(row)}
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
